/**
 * BrandingProvider — runtime branding (PRD 1, Story 1.7.3).
 *
 * Busca a config de branding do tenant via `GET /api/v1/admin/branding`,
 * expõe `useBranding()` com `branding`, `patch()`, `refetch()` e injeta
 * CSS custom properties no `:root` do documento sempre que a config muda
 * — assim a tela de admin (1.7.3) consegue alterar uma cor e ver o
 * efeito refletido em runtime sem reload (AC da story).
 *
 * Por que tolera 403:
 *   O Provider fica montado dentro de `<NotificationProvider>` no App.tsx,
 *   o que significa que TODO usuário autenticado dispara o GET — mas a
 *   permissão `admin:branding:manage` é restrita. Em vez de fazer um
 *   endpoint público duplicado só pra leitura (story futura), tratamos
 *   403 como "user sem privilégio → cai nos defaults do CSS".
 *
 * Por que CSS variables em vez de Tailwind theme override:
 *   Tailwind precisa rebuild pra mudar token. CSS vars são inspecionadas
 *   pelo browser em runtime. A story exige "sem reload", então `:root`
 *   styles são a forma mais barata. Componentes consumidores podem usar
 *   `bg-[var(--brand-primary)]` ou similar nas próximas stories.
 *
 * Refs:
 *   - Endpoint: V1/backend/app/api/v1/endpoints/branding.py (Story 1.7.2)
 *   - Schemas:  V1/backend/app/schemas/branding.py
 *   - Model:    V1/backend/app/models/tenant_branding_config.py (Story 1.7.1)
 */

import React, { createContext, useContext, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import apiClient from "@/lib/apiClient";

export interface LoginSlide {
  title: string;
  description: string;
  image_url: string;
}

export interface Branding {
  id: string;
  company_id: string;
  app_name: string;
  short_name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  pwa_theme_color: string;
  pwa_background_color: string;
  login_slides: LoginSlide[];
  email_template_overrides: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Story E19.1.1 — slogan opcional do tenant exibido sob a saudação na Home.
  // Backend ainda não retorna; tipo é optional pra Greeting já consumir quando vier.
  tagline?: string | null;
}

export type BrandingPatch = Partial<{
  app_name: string;
  short_name: string;
  logo_url: string;
  primary_color: string;
  secondary_color: string;
  pwa_theme_color: string;
  pwa_background_color: string;
  login_slides: LoginSlide[];
  email_template_overrides: Record<string, unknown>;
}>;

interface BrandingContextValue {
  branding: Branding | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  patch: (payload: BrandingPatch) => Promise<Branding>;
  isPatching: boolean;
}

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

export const BRANDING_QUERY_KEY = ["branding", "config"] as const;

// apiClient já tem baseURL terminando em `/api/v1` — NÃO prefixar aqui senão
// vira `/api/v1/api/v1/admin/branding` → 404 silencioso (bug histórico 2026-05-18).
const BRANDING_ENDPOINT = "/admin/branding";

async function fetchBranding(): Promise<Branding | null> {
  try {
    const res = await apiClient.get<Branding>(BRANDING_ENDPOINT);
    return res.data;
  } catch (err) {
    const status = (err as AxiosError | undefined)?.response?.status;
    // 403 = user sem `admin:branding:manage` — cai nos defaults do CSS.
    // 401 já é tratado pelo interceptor global do apiClient (logout).
    if (status === 403) return null;
    throw err;
  }
}

const CSS_VAR_MAP: Record<keyof Pick<Branding,
  "primary_color" | "secondary_color" | "pwa_theme_color" | "pwa_background_color"
>, string> = {
  primary_color: "--brand-primary",
  secondary_color: "--brand-secondary",
  pwa_theme_color: "--brand-pwa-theme",
  pwa_background_color: "--brand-pwa-background",
};

export function applyBrandingCssVars(b: Branding | null): void {
  const root = document.documentElement;
  if (!b) {
    Object.values(CSS_VAR_MAP).forEach((cssVar) => root.style.removeProperty(cssVar));
    return;
  }
  (Object.entries(CSS_VAR_MAP) as Array<[keyof typeof CSS_VAR_MAP, string]>).forEach(
    ([key, cssVar]) => root.style.setProperty(cssVar, b[key])
  );
}

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: BRANDING_QUERY_KEY,
    queryFn: fetchBranding,
    staleTime: 60_000,
    // retry herda do QueryClient global (App.tsx: retry=1).
    // Em tests, wrappers customizam pra retry=false e isError sai imediato.
  });

  const branding = data ?? null;

  useEffect(() => {
    applyBrandingCssVars(branding);
  }, [branding]);

  const mutation = useMutation({
    mutationFn: async (payload: BrandingPatch): Promise<Branding> => {
      const res = await apiClient.patch<Branding>(BRANDING_ENDPOINT, payload);
      return res.data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(BRANDING_QUERY_KEY, updated);
    },
  });

  const value: BrandingContextValue = {
    branding,
    isLoading,
    isError,
    refetch: () => {
      void refetch();
    },
    patch: mutation.mutateAsync,
    isPatching: mutation.isPending,
  };

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = (): BrandingContextValue => {
  const ctx = useContext(BrandingContext);
  if (ctx === undefined) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return ctx;
};

export default BrandingContext;
